/**
 * Zustand store for the Process Viewer (Build phase).
 *
 * Manages model loading, node selection, view state, and execution polling.
 * Polling uses module-level variables (not Zustand state) to survive phase switches.
 */

import { create } from 'zustand';
import api, { getWorkspaceState } from '../apiService';
import type { ModelDefinition, ModelRun, ModelRunSummary } from '../types/modeling';
import type { DictWorkspaceState } from '../types/workspaceState';
import { useProjectStore } from './projectStore';

// =============================================================================
// Module-level polling state (survives component unmount / phase switches)
// =============================================================================

let pollingIntervalId: ReturnType<typeof setInterval> | null = null;
let pollingAbortController: AbortController | null = null;

// =============================================================================
// Store types
// =============================================================================

export type ViewState = 'dag' | 'tool';
export type ModelLoadState = 'idle' | 'loading' | 'empty' | 'error';
export type InfoBarCollapse = 'expanded' | 'minimal';
export type PollingPhase = 'fast' | 'slow' | 'stopped';

interface ProcessViewerState {
  // Data
  projectId: string | null;
  workspaceId: string | null;
  userId: string | null;
  model: ModelDefinition | null;
  lastRun: ModelRun | null;
  runHistory: ModelRunSummary[];
  topologicalOrder: string[];

  // UI
  viewState: ViewState;
  selectedNodeId: string | null;
  selectedTabGid: string | null;
  infoBarCollapse: InfoBarCollapse;

  // Loading
  modelLoadState: ModelLoadState;
  modelLoadError: string | null;

  // Execution
  activeRunId: string | null;
  activeRunProgress: ModelRun | null;
  pollingPhase: PollingPhase;
  pollCount: number;
  executionError: string | null;
  isGeneratingDashboard: boolean;
  dashboardError: string | null;

  // Actions
  loadFirstModel: (projectId: string, token: string, userId?: string, workspaceId?: string) => Promise<void>;
  refreshModel: (projectId: string, token: string, workspaceId?: string) => Promise<void>;
  loadModel: (projectId: string, modelId: string, token: string) => Promise<void>;
  selectNode: (nodeId: string) => void;
  clearSelection: () => void;
  openTool: (nodeId?: string) => void;
  backToDAG: () => void;
  navigateToNextNode: () => void;
  navigateToPrevNode: () => void;
  selectTab: (gid: string | null) => void;
  setInfoBarCollapse: (state: InfoBarCollapse) => void;
  /** Optimistically reflect an upload on an INPUT_DATA node in the in-memory
   *  model so the Upload Data history list + active marker + badge update
   *  immediately — the upload persists to the report-library model store, which
   *  the frontend's workspace-event-log model doesn't mirror, so without this
   *  the just-uploaded file wouldn't appear in the panel this session. */
  applyNodeUpload: (modelId: string, nodeId: string, uploadRecord: any | null, activeUploadId: string) => void;
  setNodeActiveUpload: (modelId: string, nodeId: string, activeUploadId: string) => void;
  executeModel: (projectId: string, modelId: string, token: string) => Promise<void>;
  generateDashboard: (projectId: string, modelId: string, token: string, runId?: string | null, visualizationPrompt?: string | null) => Promise<boolean>;
  stopPolling: () => void;
  resetStore: () => void;
}

// =============================================================================
// Helpers
// =============================================================================

function computeTopologicalOrder(model: ModelDefinition): string[] {
  const adj: Record<string, string[]> = {};
  const inDegree: Record<string, number> = {};

  for (const node of model.nodes) {
    adj[node.node_id] = [];
    inDegree[node.node_id] = 0;
  }

  for (const conn of model.connections) {
    if (adj[conn.from_node_id]) {
      adj[conn.from_node_id].push(conn.to_node_id);
    }
    if (conn.to_node_id in inDegree) {
      inDegree[conn.to_node_id] += 1;
    }
  }

  const queue = Object.entries(inDegree)
    .filter(([, deg]) => deg === 0)
    .map(([id]) => id)
    .sort();

  const order: string[] = [];
  while (queue.length > 0) {
    const nid = queue.shift()!;
    order.push(nid);
    for (const neighbor of (adj[nid] || []).sort()) {
      inDegree[neighbor] -= 1;
      if (inDegree[neighbor] === 0) {
        queue.push(neighbor);
      }
    }
  }

  return order.length === model.nodes.length ? order : model.nodes.map(n => n.node_id);
}

type ModelListItem = {
  model_id?: string;
  name?: string;
  status?: string;
  node_count?: number;
  created_at?: string;
  updated_at?: string;
};

function comparePopulatedModels(a: ModelListItem, b: ModelListItem): number {
  const an = (a.node_count ?? 0) as number;
  const bn = (b.node_count ?? 0) as number;
  if (an !== bn) return bn - an;
  const au = (a.updated_at || a.created_at || '') as string;
  const bu = (b.updated_at || b.created_at || '') as string;
  return bu.localeCompare(au);
}

function isNamedModelA(model: ModelListItem): boolean {
  const name = model.name || '';
  return (model.node_count ?? 0) > 0 && /\bmodel\s+a\s*(?:[\u2012-\u2015-]|$)/i.test(name);
}

export function selectDefaultModel(models: ModelListItem[]): ModelListItem | undefined {
  // Explicit lifecycle state is still the strongest signal.
  const active = models.find((m) => m.status === 'active');
  if (active) return active;

  // NMD build workspaces can contain an older generic prototype model plus
  // Model A/B/A2 variants. When no model has been promoted to active yet,
  // start users on the primary populated Model A variant instead of the older
  // prototype that happens to have the highest node count.
  const modelA = models.filter(isNamedModelA).sort(comparePopulatedModels)[0];
  if (modelA) return modelA;

  return [...models].sort(comparePopulatedModels)[0];
}

export function selectWorkspaceModel(
  workspace: DictWorkspaceState | null | undefined,
  workspaceId: string | undefined,
  modelId: string,
): ModelDefinition | null {
  if (!workspaceId || workspace?.workspace_id !== workspaceId) return null;
  return workspace.models?.[modelId] ?? null;
}

async function loadWorkspaceStateModel(
  workspaceId: string | undefined,
  userId: string | undefined,
  modelId: string,
): Promise<ModelDefinition | null> {
  const currentWorkspace = useProjectStore.getState().dictWorkspace;
  const currentModel = selectWorkspaceModel(currentWorkspace, workspaceId, modelId);
  if (currentModel) return currentModel;

  if (!workspaceId || !userId) return null;

  const result = await getWorkspaceState(workspaceId, userId);
  if (!result.success || !result.state) return null;
  return result.state.models?.[modelId] ?? null;
}

/** Overlay INPUT_DATA upload metadata (active_upload_id + uploads history) from
 *  the report-library model (`src`) onto the workspace-event-log model (`base`).
 *  The HTTP upload endpoint persists uploads to the report-library store, which
 *  the event-log model doesn't mirror — so without this, uploads vanish from the
 *  panel on reload. Only the two upload fields on input_data nodes are copied;
 *  everything else stays from the canonical event-log model. Best-effort. */
function overlayInputUploadMetadata(base: ModelDefinition, src: any): ModelDefinition {
  if (!src || !Array.isArray(src.nodes)) return base;
  const srcById: Record<string, any> = {};
  src.nodes.forEach((n: any) => { if (n && n.node_id) srcById[n.node_id] = n; });
  const nodes = base.nodes.map((n) => {
    if (n.node_type !== 'input_data') return n;
    const sMeta = (srcById[n.node_id] && srcById[n.node_id].metadata) || null;
    if (!sMeta || (sMeta.active_upload_id === undefined && sMeta.uploads === undefined)) return n;
    const meta: any = { ...(n.metadata || {}) };
    if (sMeta.active_upload_id !== undefined) meta.active_upload_id = sMeta.active_upload_id;
    if (sMeta.uploads !== undefined) meta.uploads = sMeta.uploads;
    return { ...n, metadata: meta };
  });
  return { ...base, nodes };
}

function dashboardErrorMessage(error: any): string {
  if (error?.status === 404) {
    return error.message || 'No completed or failed run was found for this model.';
  }
  if (error?.status === 409) {
    return error.message || 'A report visualization is already being generated, or the selected run is still running.';
  }
  return error?.message || 'Failed to generate report visualization.';
}


// =============================================================================
// Initial state
// =============================================================================

const initialState = {
  projectId: null as string | null,
  workspaceId: null as string | null,
  userId: null as string | null,
  model: null as ModelDefinition | null,
  lastRun: null as ModelRun | null,
  runHistory: [] as ModelRunSummary[],
  topologicalOrder: [] as string[],
  viewState: 'dag' as ViewState,
  selectedNodeId: null as string | null,
  selectedTabGid: null as string | null,
  infoBarCollapse: 'expanded' as InfoBarCollapse,
  modelLoadState: 'idle' as ModelLoadState,
  modelLoadError: null as string | null,
  activeRunId: null as string | null,
  activeRunProgress: null as ModelRun | null,
  pollingPhase: 'stopped' as PollingPhase,
  pollCount: 0,
  executionError: null as string | null,
  isGeneratingDashboard: false,
  dashboardError: null as string | null,
};

// =============================================================================
// Store
// =============================================================================

export const useProcessViewerStore = create<ProcessViewerState>((set, get) => ({
  ...initialState,

  loadFirstModel: async (projectId: string, token: string, userId?: string, workspaceId?: string) => {
    const state = get();
    if (state.projectId === projectId && state.workspaceId === (workspaceId ?? null) && state.model) {
      console.log('[ProcessViewer] loadFirstModel skip — already loaded', {
        projectId, workspaceId, currentModelId: state.model.model_id,
      });
      return;
    }

    const uid = userId || state.userId || undefined;
    console.log('[ProcessViewer] loadFirstModel start', { projectId, workspaceId, uid });
    set({
      modelLoadState: 'loading',
      modelLoadError: null,
      projectId,
      workspaceId: workspaceId ?? null,
      ...(uid ? { userId: uid } : {}),
    });

    try {
      const result = await api.getModels(projectId, token, uid, undefined, workspaceId);
      const models = result.models || result || [];

      if (!Array.isArray(models) || models.length === 0) {
        console.log('[ProcessViewer] loadFirstModel empty — no models in workspace', {
          projectId, workspaceId,
        });
        set({ modelLoadState: 'empty', model: null });
        return;
      }

      // Pick the best default model. Order:
      //  1. an explicitly active one
      //  2. a populated "Model A" variant when the workspace has A/B/A2 models
      //  3. the most-populated draft (highest node_count, ties broken by
      //     newest updated_at) — guards against the case where the agent
      //     created several stub models before the one it actually built
      //     out, leaving multiple drafts in the listing.
      const target = selectDefaultModel(models);
      if (!target?.model_id) {
        set({ modelLoadState: 'empty', model: null });
        return;
      }
      console.log('[ProcessViewer] loadFirstModel picked', {
        modelId: target.model_id,
        modelName: target.name,
        status: target.status,
        nodeCount: target.node_count,
        candidateCount: models.length,
      });

      await get().loadModel(projectId, target.model_id, token);
    } catch (error: any) {
      console.error('[ProcessViewer] loadFirstModel failed', error);
      const isAuth = error?.status === 401;
      set({
        modelLoadState: 'error',
        modelLoadError: isAuth
          ? 'Session expired — please log in again to view models.'
          : (error.message || 'Failed to load models'),
      });
    }
  },

  refreshModel: async (projectId: string, token: string, workspaceId?: string) => {
    const uid = get().userId || undefined;
    set({ model: null, projectId: null, workspaceId: null });
    await get().loadFirstModel(projectId, token, uid, workspaceId);
  },

  loadModel: async (projectId: string, modelId: string, token: string) => {
    const uid = get().userId || undefined;
    // Carry the workspace_id so the backend reads from the canonical
    // event-log derived state (round-11 fix). When workspaceId is null
    // (e.g. a research-only project that doesn't have a production
    // workspace), the backend falls back to its legacy project-Firestore
    // path. The two paths used to diverge silently — see round-11 commit.
    const wsid = get().workspaceId || undefined;
    set({ modelLoadState: 'loading', modelLoadError: null, projectId });

    try {
      let model: ModelDefinition | null = await loadWorkspaceStateModel(wsid, uid, modelId);
      if (!model) {
        // No event-log model — the report-library model IS the source.
        model = await api.getModel(projectId, modelId, token, uid, wsid);
      } else {
        // Have the canonical event-log model, but it doesn't mirror INPUT_DATA
        // uploads (the HTTP upload endpoint writes them to the report-library
        // model store). Overlay that upload metadata so uploads survive reload.
        try {
          const rlModel = await api.getModel(projectId, modelId, token, uid, wsid);
          model = overlayInputUploadMetadata(model, rlModel);
        } catch {
          /* best-effort: keep the event-log model as-is */
        }
      }
      if (!model) throw new Error('Model not found');
      const topo = computeTopologicalOrder(model);

      // Try to load latest run
      let lastRun: ModelRun | null = null;
      try {
        const runsResult = await api.getModelRuns(projectId, modelId, token, uid);
        const runs = runsResult.runs || runsResult || [];
        if (Array.isArray(runs) && runs.length > 0) {
          // Get the most recent run details
          const latestRunSummary = runs[0];
          lastRun = await api.getModelRun(projectId, modelId, latestRunSummary.run_id, token, uid);
        }
      } catch {
        // No runs yet — that's fine
      }

      set({
        model,
        lastRun,
        topologicalOrder: topo,
        modelLoadState: 'idle',
        modelLoadError: null,
        executionError: null,
        // Reset UI state for new model
        selectedNodeId: null,
        viewState: 'dag',
        infoBarCollapse: 'expanded',
      });
    } catch (error: any) {
      set({ modelLoadState: 'error', modelLoadError: error.message || 'Failed to load model' });
    }
  },

  selectNode: (nodeId: string) => {
    set({ selectedNodeId: nodeId, infoBarCollapse: 'expanded' });
  },

  clearSelection: () => {
    set({ selectedNodeId: null, infoBarCollapse: 'expanded' });
  },

  applyNodeUpload: (modelId, nodeId, uploadRecord, activeUploadId) => {
    set((state) => {
      if (!state.model || state.model.model_id !== modelId) return {};
      const nodes = state.model.nodes.map((n) => {
        if (n.node_id !== nodeId) return n;
        const meta: any = { ...(n.metadata || {}) };
        const uploads = Array.isArray(meta.uploads) ? meta.uploads.slice() : [];
        if (uploadRecord && uploadRecord.upload_id
            && !uploads.some((u: any) => u && u.upload_id === uploadRecord.upload_id)) {
          uploads.push(uploadRecord);
        }
        meta.uploads = uploads;
        if (activeUploadId) meta.active_upload_id = activeUploadId;
        return { ...n, metadata: meta };
      });
      return { model: { ...state.model, nodes } };
    });
  },

  setNodeActiveUpload: (modelId, nodeId, activeUploadId) => {
    set((state) => {
      if (!state.model || state.model.model_id !== modelId) return {};
      const nodes = state.model.nodes.map((n) => {
        if (n.node_id !== nodeId) return n;
        return { ...n, metadata: { ...(n.metadata || {}), active_upload_id: activeUploadId } };
      });
      return { model: { ...state.model, nodes } };
    });
  },

  openTool: (nodeId?: string) => {
    const updates: Partial<ProcessViewerState> = { viewState: 'tool' };
    if (nodeId) updates.selectedNodeId = nodeId;
    set(updates);
  },

  backToDAG: () => {
    set({ viewState: 'dag' });
  },

  navigateToNextNode: () => {
    const { topologicalOrder, selectedNodeId } = get();
    if (topologicalOrder.length === 0) return;
    if (!selectedNodeId) {
      set({ selectedNodeId: topologicalOrder[0] });
      return;
    }
    const idx = topologicalOrder.indexOf(selectedNodeId);
    if (idx < topologicalOrder.length - 1) {
      set({ selectedNodeId: topologicalOrder[idx + 1] });
    }
  },

  navigateToPrevNode: () => {
    const { topologicalOrder, selectedNodeId } = get();
    if (topologicalOrder.length === 0) return;
    if (!selectedNodeId) {
      set({ selectedNodeId: topologicalOrder[topologicalOrder.length - 1] });
      return;
    }
    const idx = topologicalOrder.indexOf(selectedNodeId);
    if (idx > 0) {
      set({ selectedNodeId: topologicalOrder[idx - 1] });
    }
  },

  selectTab: (gid: string | null) => {
    set({ selectedTabGid: gid });
  },

  setInfoBarCollapse: (state: InfoBarCollapse) => {
    set({ infoBarCollapse: state });
  },

  executeModel: async (projectId: string, modelId: string, token: string) => {
    // Stop any existing polling
    get().stopPolling();
    const uid = get().userId || undefined;
    // NB: the required-input check is enforced authoritatively by the BACKEND
    // gate (execution.py reads the report-library model — the store the upload
    // actually writes — and returns a clean 422 listing any input node still
    // missing its data). We deliberately do NOT pre-check here off the frontend
    // model: that's the workspace-event-log copy, which the HTTP upload endpoint
    // doesn't update, so it was false-blocking runs whose data WAS uploaded.
    try {
      set({ executionError: null });
      const result = await api.executeModel(projectId, modelId, token, uid);
      const runId = result.run_id;

      set({
        activeRunId: runId,
        pollingPhase: 'fast',
        pollCount: 0,
      });

      // Start module-level polling
      pollingAbortController = new AbortController();
      let count = 0;
      const startTime = Date.now();

      const poll = async () => {
        if (pollingAbortController?.signal.aborted) return;
        // BAR-53: bail if the store's active project no longer matches the
        // project this run was launched for (the `projectId` arg) — a finishing
        // run must not write into the project the user switched to. Comparing to
        // the arg (not a store snapshot) is unambiguous about the run's owner.
        if (get().projectId !== projectId) { get().stopPolling(); return; }

        try {
          const run: ModelRun = await api.getModelRun(projectId, modelId, runId, token, uid);
          count++;

          if (run.status === 'completed' || run.status === 'failed') {
            // Atomic completion transition
            get().stopPolling();
            set({
              activeRunProgress: null,
              lastRun: run,
              activeRunId: null,
              pollingPhase: 'stopped',
              pollCount: 0,
            });
            // Refresh workspace state so the UI reflects anything the run
            // produced — output documents (Generated documents) and dashboard
            // results (Results) — which otherwise never appear until reload.
            const wsId = get().workspaceId || undefined;
            const uidForRefresh = get().userId || undefined;
            if (wsId && uidForRefresh) {
              useProjectStore.getState().loadSpecificWorkspace(wsId, uidForRefresh, { showLoading: false, fresh: true });
            }
            return;
          }

          // Update progress
          set({ activeRunProgress: run, pollCount: count });

          // Phase transition: fast (500ms × 20) → slow (2000ms)
          if (count === 20 && pollingIntervalId) {
            clearInterval(pollingIntervalId);
            set({ pollingPhase: 'slow' });
            pollingIntervalId = setInterval(poll, 2000);
          }

          // Timeout at 90s
          if (Date.now() - startTime > 90000) {
            get().stopPolling();
            set({ pollingPhase: 'stopped' });
          }
        } catch {
          // Polling error — ignore if aborted, otherwise stop
          if (!pollingAbortController?.signal.aborted) {
            get().stopPolling();
          }
        }
      };

      // Start fast polling (500ms)
      pollingIntervalId = setInterval(poll, 500);
    } catch (error: any) {
      console.error('[ProcessViewer] Execute failed:', error);
      set({ executionError: error?.message || 'Failed to execute model' });
    }
  },

  generateDashboard: async (projectId: string, modelId: string, token: string, runId?: string | null, visualizationPrompt?: string | null) => {
    get().stopPolling();
    const uid = get().userId || undefined;

    set({
      isGeneratingDashboard: true,
      dashboardError: null,
      pollingPhase: 'fast',
      pollCount: 0,
    });

    try {
      const result = await api.generateDashboard(projectId, modelId, token, uid, runId, visualizationPrompt);
      const dashboardRunId = result.run_id;

      pollingAbortController = new AbortController();
      let count = 0;
      const startTime = Date.now();

      return await new Promise<boolean>((resolve) => {
        let settled = false;

        const cleanup = () => {
          if (pollingIntervalId) {
            clearInterval(pollingIntervalId);
            pollingIntervalId = null;
          }
          pollingAbortController = null;
        };

        const finish = async (success: boolean, errorMessage: string | null = null) => {
          if (settled) return;
          settled = true;
          cleanup();
          set({
            isGeneratingDashboard: false,
            dashboardError: errorMessage,
            pollingPhase: 'stopped',
            pollCount: 0,
          });

          if (success) {
            const wsId = get().workspaceId || undefined;
            const uidForRefresh = get().userId || undefined;
            if (wsId && uidForRefresh) {
              await useProjectStore.getState().loadSpecificWorkspace(wsId, uidForRefresh, { showLoading: false, fresh: true });
            }
          }
          resolve(success);
        };

        pollingAbortController?.signal.addEventListener('abort', () => {
          if (settled) return;
          settled = true;
          cleanup();
          set({
            isGeneratingDashboard: false,
            pollingPhase: 'stopped',
            pollCount: 0,
          });
          resolve(false);
        }, { once: true });

        const poll = async () => {
          if (pollingAbortController?.signal.aborted) return;
          if (get().projectId !== projectId) { await finish(false); return; }

          try {
            const run: ModelRun = await api.getModelRun(projectId, modelId, dashboardRunId, token, uid);
            count++;

            if (run.dashboard_status === 'completed') {
              await finish(true);
              return;
            }
            if (run.dashboard_status === 'failed') {
              await finish(false, run.dashboard_error || 'Report visualization generation failed.');
              return;
            }

            set({ pollCount: count });

            if (count === 20 && pollingIntervalId) {
              clearInterval(pollingIntervalId);
              set({ pollingPhase: 'slow' });
              pollingIntervalId = setInterval(poll, 2000);
            }

            if (Date.now() - startTime > 120000) {
              await finish(false, 'Report visualization generation timed out. Refresh or try again.');
            }
          } catch (error: any) {
            if (!pollingAbortController?.signal.aborted) {
              await finish(false, dashboardErrorMessage(error));
            }
          }
        };

        pollingIntervalId = setInterval(poll, 500);
      });
    } catch (error: any) {
      const message = dashboardErrorMessage(error);
      console.error('[ProcessViewer] Dashboard generation failed:', error);
      set({
        isGeneratingDashboard: false,
        dashboardError: message,
        pollingPhase: 'stopped',
        pollCount: 0,
      });
      return false;
    }
  },

  stopPolling: () => {
    if (pollingAbortController) {
      pollingAbortController.abort();
      pollingAbortController = null;
    }
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      pollingIntervalId = null;
    }
    set({ pollingPhase: 'stopped', isGeneratingDashboard: false });
  },

  resetStore: () => {
    get().stopPolling();
    set({ ...initialState });
  },
}));
